import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  EyeIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const DocumentViewer = ({ document, onClose, onDownload }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoryColor = (category) => {
    const colors = {
      policy: 'bg-blue-100 text-blue-800',
      form: 'bg-green-100 text-green-800',
      template: 'bg-purple-100 text-purple-800',
      procedure: 'bg-orange-100 text-orange-800',
      guideline: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel')) return '📊';
    if (mimeType.includes('image')) return '🖼️';
    return '📄';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-lg">{getFileIcon(document.mimeType)}</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{document.title}</h3>
                <p className="text-sm text-gray-500">{document.fileName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            {/* Document Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Document Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Category:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(document.category)}`}>
                        {document.category}
                      </span>
                    </div>
                    {document.subcategory && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Subcategory:</span>
                        <span className="text-sm text-gray-900">{document.subcategory}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">File Size:</span>
                      <span className="text-sm text-gray-900">{formatFileSize(document.fileSize)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">File Type:</span>
                      <span className="text-sm text-gray-900">{document.mimeType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Version:</span>
                      <span className="text-sm text-gray-900">{document.version || '1.0'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Visibility:</span>
                      <div className="flex items-center space-x-1">
                        {document.isPublic ? (
                          <>
                            <GlobeAltIcon className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-600">Public</span>
                          </>
                        ) : (
                          <>
                            <LockClosedIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">Private</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {document.description || 'No description available'}
                  </p>
                </div>

                {document.tags && document.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {document.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Activity</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Views:</span>
                      <span className="text-sm text-gray-900">{document.viewCount || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Downloads:</span>
                      <span className="text-sm text-gray-900">{document.downloadCount || 0}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Timeline</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Created:</span>
                      <span className="text-sm text-gray-900">{formatDate(document.createdAt)}</span>
                    </div>
                    {document.publishedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Published:</span>
                        <span className="text-sm text-gray-900">{formatDate(document.publishedAt)}</span>
                      </div>
                    )}
                    {document.updatedAt && document.updatedAt !== document.createdAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Updated:</span>
                        <span className="text-sm text-gray-900">{formatDate(document.updatedAt)}</span>
                      </div>
                    )}
                    {document.expiryDate && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Expires:</span>
                        <span className={`text-sm ${new Date(document.expiryDate) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatDate(document.expiryDate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Created By</h4>
                  <div className="flex items-center space-x-2">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {document.CreatedBy?.firstName} {document.CreatedBy?.lastName}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Preview */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Document Preview</h4>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-600 mb-4">
                  Document preview is not available. Click the download button to view the file.
                </p>
                <button
                  onClick={onDownload}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                  Download Document
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Close
              </button>
              <button
                onClick={onDownload}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <ArrowDownTrayIcon className="w-4 h-4 inline mr-2" />
                Download
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DocumentViewer; 